
// newtypes. the "unique symbol" makes it a static type error to pass mismatched types 
// e.g. PostId where PrincipalId is epxected
// see more https://kubyshkin.name/posts/newtype-in-typescript/

import { FeedbackTag } from "./PublicFeedback";

// these newtypes are erased at runtime and are a zero-cost abstraction
// export type PostId = number & { readonly __tag: unique symbol };
// export type PrincipalId = number & { readonly __tag: unique symbol };
// export type TagId = number & { readonly __tag: unique symbol };

export type PostId = number;
export type RootPostId = number;
export type PrincipalId = number;
export type TagId = number;

export enum PostType {
    problem = 0,
    suggestion = 1,
    comment = 2,
    solution = 3,
    workaround = 4,
}

export enum PostAction {
    add_tag = 0,                // add a tag (space, state, generic, ...)
    convert = 1,                // Convert - change type problem/suggestion and /comment/solution/workaround
    create = 2,                 // create posts (comment, solution, ...)
    delete = 3,                 // when problem/suggestion/comment/solution/workaround is deleted
    edit_text = 4,              // Edit description of problem/suggestion/comment/solution/workaround
    edit_title = 5,             // title update
    link = 6,                   // Link. e.g. duplicate, split, link to GitHub, AzDo
    react = 7,                  // Reactions to closure, votes, thumbs up/down
    remove_tag = 8,             // remove tag (space, state, generic, ...). for state change, add/remove will have the same CreatedDate
    unlink = 9,                 // Unlink. e.g. duplicate, split, link to GitHub, AzDo
    view = 10,                  // Someone (may be anonymous) viewed a post. Only used for top-level posts.
    follow = 11,                // Someone followed a post
    unfollow = 12,              // Someone unfollowed a post
    set_moderation_status = 13, // Flag post for moderation    
    anonymize_post = 14,        // Anonymize post author/commentor due to GDPR request
    anonymize_mentions = 15,    // Anonymize mentions in post due to GDPR request

    auto_tag = 16,              // Info about how a post got assigned tags. Used to trace routing reasons for posts
    route = 17,                 // Routing info about how a post got assigned an area path. Used to trace routing reasons for posts
}

export enum TagCategory {
    system = 0, // nmi_comment, official_solution, incident
    state = 1, // closed, duplicate, ...
    severity = 2, // cant_use, frustrated, nice_to_have, ...
    space = 3, // Visual Studio, Visual Studio for Mac
    generic = 4, // fixed_in/Visual Studio 2019 version 16.4
    environment = 5, // product_build/12345, os/Windows 10.12345 (not in AH)
    tenet = 6, // Performance, Secutiry, Accessibility, ...
}

/**
State tags. Note: the ID of the state tag matches the TagId
 */
export enum StateTag {
    unknown = -1,

    new = 320,
    triaged = 321,
    consideration = 322,
    investigation = 323,

    more_info = 324,
    pending = 325,
    closed = 326,

    fixed = 327,
    duplicate = 328,
    lower_priority = 329,
    not_enough_info = 330,
    not_a_bug = 331,
    other_product = 332,
    out_of_scope = 333,

    // EngCom-only states:
    active = 334,
    closed_by_design = 335,
    closed_not_repro = 336,
    closed_obsolete = 337,
    wont_fix = 338,
    will_not_fix = 257,
    completed = 339,
    on_backlog = 356,
}

export enum Users {
    Anonymous = 2,
}

export enum ReactionType {
    upvote = 0,
    downvote = 1,
    react_yes = 2,
    react_no = 3,
}

export enum SystemTags {
    /**
    Need more info comment
    */
    nmi_comment = 300,

    /**
    Post with this tag is Incident
    */
    incident = 301,

    /**
    Comment with this tag is "unread by Microsoft". Used for unread reactions, however could be used for more in the future.
    This is used to show reactions that need attention.
    */
    comment_unread = 302,

    /**
    Comment with this tag is explaining a "yes" reaction. Used to cover comments from legacy DB, may be removed in the future.
    The content of these comments is ignored for displaying, however we show a "Yes it worked" instead.
    Legacy DB code would set the comment text as "Yes it worked" for back-compat with very old clients.
    */
    comment_react_yes = 303,

    /**
    Comment with this tag is explaining a "no" reaction
    Used to display the comment as a "reaction" rather than comment.
    */
    comment_react_no = 304,

    /**
    A solution with this tag was proposed by Microsoft when closing feedback. 
    Used because a person may leave Microsoft, thus we may not be able to determine 
    if a solution is "official" based only on person's account/group membership.
    
    A "reactivate" option will show on the last official solution for a given root post.
    
    Note: in old DB we were limited to only one of these for all solutions in a given rootPost.
    setState() was cleaning the tag for older solutions. However, in the new DB we can keep the metadata and just
    use the latest created solution as "the one". This makes setState() and other operations simpler and can fix a 
    bug where reactions would render as comments in older solutions.
    */
    solution_official = 305,

    /**
    Set on comments where the customer provided more info for a "need more info" request.
    Used to avoid notifying customers of need more info comments that they already provided info on.
    */
    comment_info_provided = 306,

    /**
    Set on comments that are considered a reply from an internal engineer who is in the role of resolving the issue
    rather than the person having the issue (e.g. a customer or engineer reporting the problem).
    Used to set replies by internal engineers to avoid asking them for "Need More Info" requests.
    
    Nikola: 2024-09-17: current logic is "not OP and sent from ADO" however that may change in the future.
    Nikola: 2021-11-10: currently only send for Azdo replies. Potentially can be expanded to include non-AzDo
    replies if we can unambiguously identify them as MS engineer replyting to help, rather than commenting on issue
    they are having. That's why this tag is not named "comment_engineer_azdo_reply"
    */
    comment_engineer_reply = 307,
}

export enum Groups {
    /// <summary>
    /// The id of the group containing all Microsoft users.
    /// </summary>
    Microsoft = 272,
    Admins = 5,
    Moderators = 4,
    Public = 3,
}

export interface Reaction {
    postId: PostId;
    type: ReactionType;
    author: Author;
    date: Date;
}

export interface HistoryItem {
    postId: PostId;
    author: Author;
    action: PostAction;
    old: string | null;
    new: string | null;
    tagId: number | null;
    linkId: PostId | null;
    date: Date;

    // computed properties:
    status: string; // computed property
}

export interface Author {
    id: PrincipalId;
    name: string;
}

export interface Tag extends FeedbackTag {
    id: TagId;
    category: number;
    slug?: string;
}

/** Used as intersection between SearchResult and RootPost to support passing in 
 * either SearchResult or RootPost as argument to functions that only need ID and trackingId
 */
export interface PartialPost {
    id: PostId, // computed property for SearchResult, comes from server for BasePost
    trackingId: string | null,
}

export interface BasePost extends PartialPost {
    author: Author;
    text: string;
    type: PostType;
    createdDate: Date;
    lastActivityDate: Date;
    isDeleted: boolean;
    isClean: boolean;
    children: ChildPost[];
    tags: Tag[];

    // Nikola: 2021-11-03: previously sent by backend, replaced with the shorter reactCounts and ownReactCounts.
    // keeping here in case we need to send richer data in the future.
    //reactions: Reaction[]; 

    reactCounts: ReactionCounts | null;
    ownReactCounts: ReactionCounts | null;
    permittedViewers: PrincipalId[] | null;

    /** true if this post is publicly visible, false if limited audience (may or may not include current user) */
    isPublic: boolean;

    // computed properties:
    createdByImageLink: string; // computed property
}

/** Contains an array of reaction counts for each ReactionType
 * For example reactCounts[ReactionType.upvote] == <number of upvotes>
 * Having an array allows easy backwards compatible extension to more reactions (flowers, rocket, etc.)
 */
export class ReactionCounts extends Array<number> {
    public static zero(): ReactionCounts { return [0, 0, 0, 0]; }
}

export interface AIData {
    summary?: AISummary;
}

export interface AISummary {
    /** Summary ID. Used to vote/comment on summary */
    id: number;

    /** Summary in markdown format. */
    text: string;

    /** Error, if any. */
    error?: string;

    /** Date when this AI summary was created.
     * Used to detect outdated summaries from client and auto-regenerate.
     */
    date: Date;

    /** True if the summary contains internal info, false if it only contains public info. */
    internal: boolean;
}

export interface RootPost extends BasePost {
    title: string;
    partialHistory: HistoryItem[];
    isFollowing: boolean | null;
    currentUserId: PrincipalId;
    intUrl: string | null; // internal ADO ticket URL, if any
    ai: AIData | null; // AI data, if any.

    // computed properties on initial load:
    totalVotes: number; // computed property
    hasVoted: boolean; // computed property
    state: string; // computed property
    stateTagId: StateTag; // computed property
    cantVote: boolean; // computed property
    cantComment: boolean; // computed property
    solutionCount: number; // computed property
    iCreatedThis: boolean; // computed property
    createdByUrl: string; // computed property
    commentCount: number; // computed property
    spaceId: number; // computed property
}

export interface ChildPost extends BasePost {
    parentId: PostId;

    // computed properties on initial load:
    canReactivate: boolean; // computed property
    couldReactivateEver: boolean; // computed property
    voteCount: number; // computed property
    userVoteType: "voteup" | "votedown" | null; // computed property
    recipientNames: string; // computed property

    /** the reaction that the post *is*, if any; not the reaction(s) associated with the post */
    reaction: "reactYes" | "reactNo" | null; // computed property
}

export function isStateTag(tagId: number): boolean {
    return tagId >= 320 && tagId <= 340;
}

export function getDefaultUserNames(): { [id: number]: string } {
    const userNames: { [id: number]: string } = {};
    userNames[Groups.Microsoft] = "Microsoft";
    userNames[Groups.Admins] = "Admins";
    userNames[Groups.Moderators] = "Moderators";
    userNames[Groups.Public] = "Public";

    return userNames
}

export function hasSystemTag(tags: Tag[], systemTag: SystemTags) {
    for (const tag of tags) {
        if (tag.id === systemTag) {
            return true;
        }
    }

    return false;
}

export function getLastNeedMoreInfoCommentId(rootPost: RootPost, defaultId: PostId): PostId {
    let lastNeedMoreInfoCommentId = defaultId;
    for (const child of rootPost.children) {
        if (child.type !== PostType.comment) {
            continue;
        }

        if (hasSystemTag(child.tags, SystemTags.nmi_comment)) {
            lastNeedMoreInfoCommentId = child.id;
        }
    }

    return lastNeedMoreInfoCommentId;
}

export function getNeedMoreInfoComments(rootPost: RootPost): ChildPost[] {
    const nmiComments: ChildPost[] = [];
    for (const child of rootPost.children) {
        if (child.type !== PostType.comment) {
            continue;
        }

        if (hasSystemTag(child.tags, SystemTags.nmi_comment)) {
            nmiComments.push(child);
        }
    }

    return nmiComments;
}

/** Used to remove all read-only tags (keeps only generic tags) to disallow editing tags such as space and severity by users */
export const removeReadOnly = (tag: Tag) => tag.category === TagCategory.generic;
